import { PropTypes } from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';

import { useIntl } from '@zenbusiness/application-commons-intl';
import isValidE164PhoneNumber from '@zenbusiness/application-commons-utils/isValidE164PhoneNumber';
import isValidEmail from '@zenbusiness/application-commons-utils/isValidEmail';

import Field from '#client/components/Field';
import Fieldset from '#client/components/Fieldset';
import PhoneTextField, { PHONE_NUMBER_MAX_LENGTH } from '#client/components/PhoneTextField';
import { buildFieldName } from '#client/utils/form';
import getDeviceType from '#client/utils/getDeviceType';

import { FIELD_NAMES } from './constants';

function ContactInfoFieldset({ fieldsConfig, namePrefix }) {
  const { formatMessage } = useIntl();
  const { control, formState } = useFormContext();
  const { isSubmitting } = formState;

  const {
    email: emailFieldProps,
    firstName: firstNameFieldProps,
    lastName: lastNameFieldProps,
    phone: phoneFieldProps
  } = fieldsConfig || {};

  const { show: showEmail = true, ...emailProps } = emailFieldProps || {};

  const FIELD = Object.freeze({
    FIRST_NAME: {
      name: buildFieldName(FIELD_NAMES.FIRST_NAME, namePrefix),
      label: formatMessage({ id: 'shopping.page.contactInfo.form.field.label.firstName' }),
      maxLength: 50
    },
    LAST_NAME: {
      name: buildFieldName(FIELD_NAMES.LAST_NAME, namePrefix),
      label: formatMessage({ id: 'shopping.page.contactInfo.form.field.label.lastName' }),
      maxLength: 50
    },
    EMAIL: {
      name: buildFieldName(FIELD_NAMES.EMAIL, namePrefix),
      label: formatMessage({ id: 'shopping.page.contactInfo.form.field.label.email' }),
      maxLength: 254
    },
    PHONE: {
      name: buildFieldName(FIELD_NAMES.PHONE, namePrefix),
      label: formatMessage({ id: 'shopping.page.contactInfo.form.field.label.phone' }),
      maxLength: PHONE_NUMBER_MAX_LENGTH
    }
  });

  const isDesktop = getDeviceType() === 'desktop';

  return (
    <Fieldset disabled={isSubmitting} gap={1}>
      <Controller
        control={control}
        name={FIELD.FIRST_NAME.name}
        render={({ field: { ref, value, ...field }, fieldState: { error } }) => (
          <Field
            {...field}
            autoFocus={isDesktop}
            {...firstNameFieldProps}
            error={error}
            inputProps={{
              autoComplete: 'given-name',
              maxLength: FIELD.FIRST_NAME.maxLength,
              'data-testid': 'contact-info-first-name',
              'data-1p-ignore': true // turn off 1Password autocomplete suggestions.
            }}
            inputRef={ref}
            label={FIELD.FIRST_NAME.label}
            value={value ?? ''}
          />
        )}
        rules={{
          required: formatMessage(
            { id: 'shopping.page.contactInfo.form.field.error.required' },
            { label: FIELD.FIRST_NAME.label }
          ),
          maxLength: {
            value: FIELD.FIRST_NAME.maxLength,
            message: formatMessage(
              { id: 'shopping.page.contactInfo.form.field.error.maxLength' },
              { label: FIELD.FIRST_NAME.label, maxLength: FIELD.FIRST_NAME.maxLength }
            )
          }
        }}
      />
      <Controller
        control={control}
        name={FIELD.LAST_NAME.name}
        render={({ field: { ref, value, ...field }, fieldState: { error } }) => (
          <Field
            {...field}
            {...lastNameFieldProps}
            error={error}
            inputProps={{
              autoComplete: 'family-name',
              maxLength: FIELD.LAST_NAME.maxLength,
              'data-testid': 'contact-info-last-name',
              'data-1p-ignore': true // turn off 1Password autocomplete suggestions.
            }}
            inputRef={ref}
            label={FIELD.LAST_NAME.label}
            value={value ?? ''}
          />
        )}
        rules={{
          required: formatMessage(
            { id: 'shopping.page.contactInfo.form.field.error.required' },
            { label: FIELD.LAST_NAME.label }
          ),
          maxLength: {
            value: FIELD.LAST_NAME.maxLength,
            message: formatMessage(
              { id: 'shopping.page.contactInfo.form.field.error.maxLength' },
              { label: FIELD.LAST_NAME.label, maxLength: FIELD.LAST_NAME.maxLength }
            )
          }
        }}
      />
      {showEmail && (
        <Controller
          control={control}
          name={FIELD.EMAIL.name}
          render={({ field: { ref, value, ...field }, fieldState: { error } }) => (
            <Field
              {...field}
              {...emailProps}
              error={error}
              inputProps={{
                autoComplete: 'email',
                'data-testid': 'contact-info-email',
                inputMode: 'email',
                maxLength: FIELD.EMAIL.maxLength
              }}
              inputRef={ref}
              label={FIELD.EMAIL.label}
              type="email"
              value={value ?? ''}
            />
          )}
          rules={{
            required: formatMessage(
              { id: 'shopping.page.contactInfo.form.field.error.required' },
              { label: FIELD.EMAIL.label }
            ),
            maxLength: {
              value: FIELD.EMAIL.maxLength,
              message: formatMessage(
                { id: 'shopping.page.contactInfo.form.field.error.maxLength' },
                { label: FIELD.EMAIL.label, maxLength: FIELD.EMAIL.maxLength }
              )
            },
            validate: (value) =>
              isValidEmail(value) ||
              formatMessage({ id: 'shopping.page.contactInfo.form.field.error.invalid.email' })
          }}
        />
      )}
      <Controller
        control={control}
        name={FIELD.PHONE.name}
        render={({ field: { ref, value, ...field }, fieldState: { error } }) => (
          <PhoneTextField
            {...field}
            {...phoneFieldProps}
            error={error}
            inputRef={ref}
            label={FIELD.PHONE.label}
            value={value ?? ''}
          />
        )}
        rules={{
          required: formatMessage(
            { id: 'shopping.page.contactInfo.form.field.error.required' },
            { label: FIELD.PHONE.label }
          ),
          maxLength: {
            value: FIELD.PHONE.maxLength,
            message: formatMessage(
              { id: 'shopping.page.contactInfo.form.field.error.maxLength' },
              { label: FIELD.PHONE.label, maxLength: FIELD.PHONE.maxLength }
            )
          },
          validate: (value) =>
            isValidE164PhoneNumber(value) ||
            formatMessage({ id: 'shopping.page.contactInfo.form.field.error.invalid.phone' })
        }}
      />
    </Fieldset>
  );
}

ContactInfoFieldset.propTypes = {
  fieldsConfig: PropTypes.shape({
    email: PropTypes.object,
    firstName: PropTypes.object,
    lastName: PropTypes.object,
    phone: PropTypes.object
  }),
  namePrefix: PropTypes.string
};

export default ContactInfoFieldset;
